import React from 'react';

/**
 * HIRE FORGE — the "this is an early version" notice.
 *
 * Product owner, 2026-08-11: *"Please put a notice on Hire forge that this is
 * an early version of the hire system and its more of a place holder than a
 * finished feature"*, then, clarifying which feature he meant: *"I was talking
 * about the Hire Forge section where people can put their items up for hire."*
 * So this is the MARKETPLACE (`/hireforge`, `server/routes/marketplace.js`,
 * `equipment_listings`) — **not** the internal hires tracking under
 * `client/src/components/hires/`, which is a different feature with a similar
 * name and was the first place this request was aimed.
 *
 * 🔑 **"Hire Forge" is the UI's own name for it, not just the owner's** —
 * `Layout.js` renders it as the sidebar section heading and abbreviates the
 * `Browse Equipment` item to `Hire Forge` in the mobile bar, and
 * `MarketplaceBrowse.js` uses it as the page `<h1>`. The component name follows
 * the interface rather than the route (`hireforge`) or the server module
 * (`marketplace`), which are three names for one feature — do not "correct" it
 * to `MarketplaceEarlyNotice`.
 *
 * ── WHY THE SECOND SENTENCE NAMES A LIMITATION ──────────────────────
 * "This is early" tells a user to be careful without telling them what to be
 * careful about, which is the weakest kind of warning. The specific, honest
 * limitation is that **nothing here books or tracks a hire**: browse, view,
 * and `Enquire` — which only reveals the owner's name, email and phone
 * (`MarketplaceListingDetail.js`) — is the whole feature. There is no booking,
 * no messaging, no availability check and no record that a hire happened. So
 * the sentence tells the reader the one thing they would otherwise assume the
 * app was doing for them.
 *
 * The generic alternative was written and offered to the owner alongside this
 * one:
 *
 *   "It works, but please don't rely on it for anything important yet."
 *
 * 🔑 **Both versions share their FIRST sentence, deliberately** — so switching
 * to the generic wording is a one-line edit of `HIRE_FORGE_EARLY_NOTICE` and
 * one line of `HireForgeEarlyNotice.test.js`, with no layout consequence.
 *
 * ── NOT DISMISSIBLE, AND THAT IS A DECISION ─────────────────────────
 * A dismissal would have to live in `localStorage` (the app's only client-side
 * preference store), so it is per-browser-profile: a new device, a cleared
 * browser or a second machine brings it straight back. That is a promise the
 * feature cannot keep, and it fails in the direction that matters — the user
 * most likely to lean on a placeholder is the one who has used it for weeks
 * and dismissed the warning on day one. It is also temporary by construction:
 * when Hire Forge stops being early, this component is deleted, and a deleted
 * component takes its notice off every device at once in a way a stored
 * dismissal never could. Finally, a dismiss control needs a 44px tap target
 * (CLAUDE.md, `CONTROL_HEIGHT`), which roughly doubles the height of a
 * two-line banner on a phone — the control would cost more page than the
 * message.
 *
 * The cost is that it is on screen on every visit, which is why the copy is
 * kept to two short sentences that read as a label rather than an interruption.
 *
 * ── STYLING ─────────────────────────────────────────────────────────
 * Amber, matching `components/teams/LinkedGagDeletedBanner.js` class for
 * class. That is the closest thing this repo has to a house banner: there is
 * **no shared banner/notice/callout primitive** in `components/ui/` — the four
 * banners in the app (`CookieConsentBanner`, `TeamInviteBanner`,
 * `LinkedGagDeletedBanner`, the unmounted `VatModeBanner`) each hand-roll their
 * own box. Copying the established class string rather than inventing a fifth
 * look is the nearest this change can get to the Code Unification Principle
 * without extracting a shared component, which would mean rewriting three
 * working banners for a copy change.
 *
 * Amber rather than blue because the notice asks the reader to hold back, and
 * a blue "tip" tint is read as decoration and ignored. Amber-50/800 is the
 * soft end of the scale — it cautions without alarming, which is the brief.
 *
 * ⚠️ **No margin is baked in.** Each caller positions it, because the pages
 * space their children three different ways (`space-y-6` on Browse, plain
 * `mb-*` on the other two) and two conflicting Tailwind margin utilities on
 * one element resolve by stylesheet order, not by the order they are written —
 * the trap already recorded against `ui/Toolbar`'s `marginClassName`.
 *
 * ── WHERE IT IS MOUNTED ─────────────────────────────────────────────
 * The three Hire Forge entry points the sidebar links to, and only those:
 * `MarketplaceBrowse` (Browse Equipment), `MarketplaceMyListings` (My
 * Listings) and `MarketplaceCreateListing` (Add Listing / Edit Listing).
 * `MarketplaceListingDetail` deliberately has none — it is only ever reached
 * by clicking a card on a page that showed the notice moments earlier, so a
 * fourth copy is the nag rather than the warning. Pinned by
 * `HireForgeEarlyNotice.test.js`, which asserts the presence on all three AND
 * the absence on the detail page, so "helpfully" adding a fourth goes red.
 */

/**
 * The notice text, as one exported string.
 *
 * 🚨 **`HireForgeEarlyNotice.test.js` TRANSCRIBES THIS LITERALLY rather than
 * importing it, and that is deliberate.** The house habit is the opposite —
 * `InactiveBadge.inactiveName.test.js` reads its strings from the module so
 * there is no second source of truth — but a test that imports the copy it is
 * checking asserts only that a string exists, and passes on any reword,
 * including one that makes a false claim. This app has already shipped an
 * invitation email carrying wrong instructions for months for exactly that
 * reason. Copy that reaches a user is a CONTRACT: changing it should cost a
 * deliberate test edit.
 */
export const HIRE_FORGE_EARLY_NOTICE =
  'Hire Forge is an early version — more of a placeholder than a finished feature. ' +
  'It does not book or track anything, so arrange hires directly with the owner.';

/**
 * @param {{className?: string}} props — `className` is appended, for the
 *   caller's margin. See the margin note above.
 */
export default function HireForgeEarlyNotice({ className = '' }) {
  return (
    <div
      data-testid="hireforge-early-notice"
      className={`px-3 py-2 text-sm rounded-lg bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800 ${className}`}
    >
      {HIRE_FORGE_EARLY_NOTICE}
    </div>
  );
}
